import { FC, memo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { parseJson } from '@medicusunion/front-utils';
import { Button, Form, Input, InputNumber, message } from 'antd';
import { InvoiceKeys, InvoiceTypes } from 'constants/invoice';
import { GetInvoiceTemplatesDoctor } from 'gql/financial/__generated__/GetInvoiceTemplatesDoctor';
import { CREATE_INVOICE } from 'gql/appointments/mutation';
import { GET_INVOICE_TEMPLATES_DOCTOR } from 'gql/financial/queries';
import { useTranslation } from 'react-i18next';
import client from 'apolloClient';
import { GET_APPOINTMENT_ITEM } from 'gql/appointments/queries';

import { Loading } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  onClose(): void;
  appointmentId: string;
}

type Field = {
  key_id: string;
  label: string;
  value: string;
};

const InvoiceTemplateFields: FC<IProps> = ({ onClose, appointmentId }) => {
  const { t } = useTranslation();

  const { data, loading: isFetching } = useQuery<GetInvoiceTemplatesDoctor>(
    GET_INVOICE_TEMPLATES_DOCTOR,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first'
    }
  );

  const [form] = Form.useForm();

  const [createInvoice, { loading: isCreating }] = useMutation(CREATE_INVOICE, {
    onCompleted() {
      message.success(t('messages.successfully_created'));
      client.refetchQueries({ include: [GET_APPOINTMENT_ITEM] });
      onClose();
    },
    onError: err => showErrorMessage(err)
  });

  const keys = data?.getInvoiceTemplatesDoctor.data?.keys || [];

  const onFinish = (values: Record<string, string>) => {
    const fields = Object.entries(values).reduce<Field[]>(
      (acc, [keyId, value]) => {
        if (value) {
          const key = keys.find(item => item?.id === keyId);

          if (key) {
            return [
              ...acc,
              {
                key_id: keyId,
                label: key.label,
                value: `${value}`
              }
            ];
          }

          return acc;
        }

        return acc;
      },
      []
    );

    createInvoice({
      variables: {
        input: {
          appointment_id: appointmentId,
          type: InvoiceTypes.template,
          fields
        }
      }
    });
  };

  const renderField = (type?: number) => {
    switch (type) {
      case InvoiceKeys.text:
        return <Input />;
      case InvoiceKeys.textarea:
        return <Input.TextArea />;
      case InvoiceKeys.number:
        return <InputNumber style={{ width: '100%' }} min={0} />;
      default:
        return null;
    }
  };

  if (isFetching) {
    return <Loading type="static" />;
  }

  return (
    <Form
      disabled={isCreating}
      form={form}
      layout="vertical"
      size="large"
      onFinish={onFinish}
    >
      {keys.map(item => (
        <Form.Item
          key={item?.id}
          label={parseJson(item?.label)?.en}
          name={item?.id}
          rules={[{ required: !!item?.required }]}
        >
          {renderField(item?.type)}
        </Form.Item>
      ))}
      <Form.Item>
        <Button onClick={form.submit} type="primary" loading={isCreating} block>
          {t('common.submit')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default memo(InvoiceTemplateFields);
